import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Popover,
  useTheme,
  alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { providerLabel, providerChipColor } from '../../utils/hostingProvider';

export function getAttackTypeColor(
  theme: { palette: { primary: { main: string }; secondary: { main: string }; warning: { main: string }; info: { main: string } } },
  attackClass?: string
): string {
  switch (attackClass?.toLowerCase()) {
    case 'application':
      return theme.palette.primary.main;
    case 'infrastructure':
      return theme.palette.secondary.main;
    case 'volumetric':
      return theme.palette.warning.main;
    default:
      return theme.palette.info.main;
  }
}

/** Min width for date/time (e.g. "3/4/2026, 10:30:15 AM") */
const DATE_WIDTH_CH = 22;
/** Extra width (ch) for attack type column to account for chip horizontal padding so targets start after the chip. */
export const ATTACK_TYPE_CHIP_PADDING_CH = 4;
const TARGET_LINE_HEIGHT = 1.43;
const TARGET_FONT_SIZE_REM = 0.875;
const TARGET_LINES = 3;
/** Line height in rem for one target row. */
const TARGET_LINE_HEIGHT_REM = TARGET_LINE_HEIGHT * TARGET_FONT_SIZE_REM;
/** Max height for targets block: 3 lines of content (2 targets + "Show more") plus gaps so "Show more" is never clipped. */
const TARGET_BLOCK_MAX_HEIGHT = `calc(${TARGET_LINES * TARGET_LINE_HEIGHT_REM}rem + 12px)`;

export interface ScenarioRowContentProps {
  scenarioName: string;
  isActive?: boolean;
  /** Target strings that are not in the allow list; show alert icon next to each. */
  targetsNotInAllowList?: string[];
  attackTypeName: string;
  attackClass: string;
  attackTypeDescription?: string;
  dateLabel: string;
  /** Optional time string; when provided, shown below the date. */
  timeLabel?: string;
  targets: string[];
  rowId: string;
  /** Min width for attack type chip in ch units (from longest name on page). */
  attackTypeChipMinWidthCh?: number;
  /** Optional hosting provider (raw value); shown as chip to the left of Last Execution. */
  hostingProvider?: string | null;
}

const ScenarioRowContent: React.FC<ScenarioRowContentProps> = ({
  scenarioName,
  isActive,
  targetsNotInAllowList = [],
  attackTypeName,
  attackClass,
  attackTypeDescription,
  dateLabel,
  timeLabel,
  targets,
  rowId,
  attackTypeChipMinWidthCh = 12,
  hostingProvider,
}) => {
  const theme = useTheme();
  const [showMoreAnchor, setShowMoreAnchor] = useState<HTMLElement | null>(null);
  const attackColor = getAttackTypeColor(theme as Theme, attackClass);
  const hasMoreTargets = targets.length > 3;
  const visibleTargets = hasMoreTargets ? targets.slice(0, 2) : targets;
  const additionalCount = targets.length - 2;
  const invalidSet = useMemo(() => new Set(targetsNotInAllowList), [targetsNotInAllowList]);

  return (
    <Box>
      {/* Line 1: name + Active chip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography
          variant="body1"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 700,
            color: 'text.primary',
            lineHeight: 1.3,
          }}
          title={scenarioName}
        >
          {scenarioName}
        </Typography>
        {isActive && (
          <Chip
            label="Active"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: '0.8rem', height: 22 }}
          />
        )}
      </Box>

      {/* Line 2: attack type chip | targets | "Not in allow list" (inline) | date + time on the right */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          width: '100%',
          minWidth: 0,
          fontFamily: '"Space Mono", monospace',
        }}
      >
        <Box sx={{ flexShrink: 0, minWidth: `${attackTypeChipMinWidthCh + ATTACK_TYPE_CHIP_PADDING_CH}ch` }}>
          {attackTypeName && (
            <Tooltip
              title={attackTypeDescription || 'No description'}
              enterDelay={300}
              leaveDelay={0}
            >
              <Chip
                label={attackTypeName}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.8rem',
                  fontFamily: '"Space Mono", monospace',
                  fontWeight: 500,
                  backgroundColor: `${attackColor}20`,
                  color: attackColor,
                  border: `1px solid ${alpha(attackColor, 0.4)}`,
                }}
              />
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxHeight: TARGET_BLOCK_MAX_HEIGHT,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
          }}
        >
          {visibleTargets.map((target, idx) => {
            const notInAllowList = invalidSet.has(target);
            return (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                {notInAllowList && (
                  <Tooltip title="This target is not in the allow list" enterDelay={300} leaveDelay={0}>
                    <WarningAmberIcon
                      sx={{
                        fontSize: '0.875rem',
                        height: '1em',
                        flexShrink: 0,
                        color: theme.palette.warning.main,
                      }}
                      aria-label="Target not in allow list"
                    />
                  </Tooltip>
                )}
                <Tooltip title={target} enterDelay={300} leaveDelay={0}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {target}
                  </Typography>
                </Tooltip>
              </Box>
            );
          })}
          {hasMoreTargets && (
            <>
              <Typography
                component="button"
                type="button"
                variant="body2"
                onClick={(e) => setShowMoreAnchor(e.currentTarget)}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textAlign: 'left',
                  alignSelf: 'flex-start',
                  flexShrink: 0,
                  lineHeight: TARGET_LINE_HEIGHT,
                  '&:hover': { textDecoration: 'underline' },
                }}
                aria-label={`Show ${additionalCount} more targets`}
              >
                Show more ({targets.length - 2})
              </Typography>
              <Popover
                open={Boolean(showMoreAnchor)}
                anchorEl={showMoreAnchor}
                onClose={() => setShowMoreAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                  paper: { sx: { marginTop: 4 } },
                }}
              >
                <Box sx={{ p: 2, pt: 2, maxHeight: 320, overflow: 'auto', overflowX: 'hidden', display: 'block' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, flexShrink: 0 }}>
                    All targets
                  </Typography>
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, listStyle: 'none' }}>
                  {targets.map((target, idx) => {
                    const notInAllowList = invalidSet.has(target);
                    return (
                      <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.75, minHeight: 24 }}>
                        {notInAllowList && (
                          <Tooltip title="This target is not in the allow list" enterDelay={300} leaveDelay={0}>
                            <WarningAmberIcon
                              sx={{ fontSize: '0.875rem', height: '1em', flexShrink: 0, color: theme.palette.warning.main }}
                              aria-label="Target not in allow list"
                            />
                          </Tooltip>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: '"Space Mono", monospace',
                            wordBreak: 'break-all',
                          }}
                        >
                          {target}
                        </Typography>
                      </Box>
                    );
                  })}
                  </Box>
                </Box>
              </Popover>
            </>
          )}
        </Box>
        <Box sx={{ flexShrink: 0, width: '12ch', minWidth: '12ch', display: 'flex', alignItems: 'left' }}>
          {hostingProvider ? (
            <Tooltip title={`Hosting: ${providerLabel(hostingProvider)}`} enterDelay={300} leaveDelay={0}>
              <Chip
                label={providerLabel(hostingProvider)}
                color={providerChipColor(hostingProvider)}
                size="small"
                variant="outlined"
                sx={{ fontFamily: '"Space Mono", monospace', height: 22, fontSize: '0.8rem' }}
              />
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: '"Space Mono", monospace' }}>
              —
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            marginLeft: 'auto',
            width: `${DATE_WIDTH_CH}ch`,
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ fontFamily: '"Space Mono", monospace' }}
          >
            {dateLabel}
          </Typography>
          {timeLabel && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontFamily: '"Space Mono", monospace' }}
            >
              {timeLabel}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ScenarioRowContent;
